// Checkout delivery address: map pin, current location, search, Saudi National Address.
//
// The map library (Leaflet, served from /assets/darat/vendor) and the city list load only
// when the customer picks Delivery. Geocoding always goes through our server
// (darat.storefront.api.geo_*), which caches and rate-limits it; the browser never calls
// a geocoder directly. A pin is optional: every field can be typed by hand.
import { refreshSelect } from "./select";
import { $, $$, api, esc, icon, isAr, store, t } from "./util";

const LEAFLET = "/assets/darat/vendor/leaflet/";
const RIYADH = [24.7136, 46.6753];
const SAVED_KEY = "darat_address";
const FIELDS = ["city", "district", "street", "building_number", "additional_number", "postal_code",
	"national_address", "unit_number", "landmark", "address_label", "lat", "lng"];

let started = false;
let map = null;
let leafletLoading = null;
let cityRows = null;

function loadLeaflet() {
	if (window.L) return Promise.resolve(window.L);
	if (leafletLoading) return leafletLoading;
	leafletLoading = new Promise((resolve, reject) => {
		const css = document.createElement("link");
		css.rel = "stylesheet"; css.href = LEAFLET + "leaflet.css";
		document.head.appendChild(css);
		const js = document.createElement("script");
		js.src = LEAFLET + "leaflet.js"; js.async = true;
		js.onload = () => resolve(window.L);
		js.onerror = reject;
		document.head.appendChild(js);
	});
	return leafletLoading;
}

export async function loadCities(form) {
	const sel = $("select[name=city]", form);
	if (!sel || cityRows) return cityRows;
	try { cityRows = JSON.parse(sessionStorage.getItem("darat_cities") || "null"); } catch (e) { /* private mode */ }
	if (!cityRows) {
		try { cityRows = await api("darat.storefront.api.delivery_cities"); } catch (e) { cityRows = []; }
		try { if (cityRows.length) sessionStorage.setItem("darat_cities", JSON.stringify(cityRows)); } catch (e) { /* quota */ }
	}
	if (!cityRows.length) {  // no list yet: free text, the server still validates
		const input = document.createElement("input");
		input.name = "city"; input.setAttribute("data-delivery-req", ""); input.setAttribute("data-geo-field", "city");
		input.autocomplete = "address-level2";
		(sel.closest(".ds-sel") || sel).replaceWith(input);  // the styled dropdown wrapper too
		return cityRows;
	}
	const sorted = cityRows.slice().sort((a, b) => (isAr ? a[1] : a[0]).localeCompare(isAr ? b[1] : b[0], isAr ? "ar" : "en"));
	sel.insertAdjacentHTML("beforeend", sorted.map(([v, a]) => `<option value="${esc(v)}">${esc(isAr ? a : v)}</option>`).join(""));
	refreshSelect(sel);  // the styled dropdown copies its options once: rebuild it with the cities
	return cityRows;
}

// ---------------------------------------------------------------- filling the form

function field(form, name) { return form.querySelector(`[name="${name}"]`); }

// A value the customer typed is never overwritten by the map; one the map filled is.
function fill(form, data, { force = false } = {}) {
	const map_ = { city: data.city, district: data.district, street: data.street, building_number: data.building_number,
		additional_number: data.additional_number, postal_code: data.postal_code, national_address: data.short_address };
	Object.entries(map_).forEach(([name, value]) => {
		const el = field(form, name);
		if (!el || !value) return;
		if (el.dataset.userEdited && !force) return;
		if (el.tagName === "SELECT" && !Array.from(el.options).some((o) => o.value === value)) return;
		el.value = value;
		el.dataset.autoFilled = "1";
		el.classList.remove("is-invalid");
		if (el.tagName === "SELECT") {  // repaint the styled dropdown, without counting it as the customer's choice
			el.dataset.filling = "1";
			el.dispatchEvent(new Event("change", { bubbles: true }));
			delete el.dataset.filling;
		}
	});
}

function setPin(form, lat, lng) {
	$("[data-geo-lat]", form).value = lat ? Number(lat).toFixed(6) : "";
	$("[data-geo-lng]", form).value = lng ? Number(lng).toFixed(6) : "";
}

function found(form, html, kind = "") {
	const p = $("[data-geo-found]", form);
	p.hidden = !html; p.innerHTML = html || ""; p.className = "ds-addr__found" + (kind ? ` is-${kind}` : "");
}

const ERR = {
	outside: () => t("الموقع خارج المملكة. حرّك الدبوس إلى عنوانك.", "That spot is outside Saudi Arabia. Move the pin to your address."),
	busy: () => t("الخدمة مشغولة، أكمل الحقول يدوياً أو حاول بعد لحظات.", "Busy right now. Fill the fields by hand or try again in a moment."),
	"not found": () => t("لم نجد عنواناً هنا. أكمل الحقول يدوياً.", "No address found here. Please fill the fields by hand."),
	invalid: () => t("العنوان المختصر 4 حروف و4 أرقام، مثل RJYB2357", "A short address is 4 letters and 4 digits, e.g. RJYB2357"),
};
const errText = (code) => (ERR[code] || (() => t("تعذّر جلب العنوان، أكمل الحقول يدوياً.", "Couldn't fetch the address. Please fill the fields by hand.")))();

let reverseTimer = null;
let lastKey = "";
function reverseSoon(form, lat, lng) {
	clearTimeout(reverseTimer);
	reverseTimer = setTimeout(async () => {
		const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;  // ~11 m: the same as the server cache
		if (key === lastKey) return;
		lastKey = key;
		found(form, `<span class="ds-spinner"></span> ${t("نقرأ العنوان من الخريطة…", "Reading the address from the map…")}`);
		try {
			const r = await api("darat.storefront.api.geo_reverse", { lat, lng }, "POST");
			if (r.error) { found(form, esc(errText(r.error)), "warn"); return; }
			fill(form, r);
			const line = [r.building_number, r.street, r.district, r.city_text].filter(Boolean).join("، ");
			found(form, `✓ ${esc(line || r.label || "")}${r.source === "osm" && !r.building_number ? `<br><small>${t("أدخل رقم المبنى من لوحة العنوان الوطني.", "Add the building number from the National Address plate.")}</small>` : ""}`, "ok");
		} catch (e) { found(form, esc(e.message), "warn"); }
	}, 700);
}

// ---------------------------------------------------------------- map

let mapReady = null;  // one map, created once, however many callers race for it

function startMap(form, center) {
	if (!mapReady) mapReady = createMap(form, center);
	return mapReady.then((m) => {
		if (m && center) m.setView(center, Math.max(m.getZoom(), 17));
		return m;
	});
}

async function createMap(form, center) {
	const box = $("[data-map]", form);
	if (!box) return null;
	let L;
	try { L = await loadLeaflet(); } catch (e) { box.closest(".ds-map").hidden = true; return null; }
	map = L.map(box, { center: center || RIYADH, zoom: center ? 17 : 11, zoomControl: true, attributionControl: true,
		scrollWheelZoom: false, tap: true });
	L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
		maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
	}).addTo(map);
	map.on("movestart", () => $("[data-map-hint]", form)?.classList.add("is-moving"));
	map.on("moveend", () => {
		$("[data-map-hint]", form)?.classList.remove("is-moving");
		if (map.getZoom() < 15) return;  // the whole city in view: not a door yet
		const c = map.getCenter();
		setPin(form, c.lat, c.lng);
		reverseSoon(form, c.lat, c.lng);
	});
	// Inside a form that was hidden until now: Leaflet must re-measure.
	setTimeout(() => map.invalidateSize(), 60);
	return map;
}

function locate(form) {
	const btn = $("[data-geo-locate]", form);
	if (!navigator.geolocation) { found(form, esc(t("المتصفح لا يدعم تحديد الموقع.", "This browser can't share your location.")), "warn"); return; }
	btn.disabled = true; btn.classList.add("is-loading");
	navigator.geolocation.getCurrentPosition(async (pos) => {
		btn.disabled = false; btn.classList.remove("is-loading");
		const { latitude: lat, longitude: lng, accuracy } = pos.coords;
		const m = await startMap(form, [lat, lng]);
		m?.setView([lat, lng], 18);
		if (accuracy > 150) found(form, esc(t("دقة الموقع ضعيفة، حرّك الخريطة لضبط الدبوس على بابك.", "Your location is approximate. Move the map so the pin is on your door.")), "warn");
	}, (err) => {
		btn.disabled = false; btn.classList.remove("is-loading");
		const msg = err.code === 1
			? t("لم يُسمح بالوصول للموقع. ابحث عن حيّك أو حرّك الخريطة، أو أكمل الحقول يدوياً.", "Location access was blocked. Search your district or move the map, or type the address.")
			: t("تعذّر تحديد موقعك الآن. ابحث عن حيّك أو حرّك الخريطة.", "Couldn't get your location. Search your district or move the map.");
		found(form, esc(msg), "warn");
	}, { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 });
}

async function search(form) {
	const q = ($("[data-geo-q]", form).value || "").trim();
	const box = $("[data-geo-results]", form);
	if (q.length < 3) return;
	box.hidden = false; box.innerHTML = `<p><span class="ds-spinner"></span></p>`;
	let rows = [];
	try { rows = await api("darat.storefront.api.geo_search", { q }, "POST"); } catch (e) { rows = []; }
	if (!rows.length) { box.innerHTML = `<p>${esc(t("لا نتائج. جرّب اسم الحي أو حرّك الخريطة.", "No results. Try the district name or move the map."))}</p>`; return; }
	box.innerHTML = rows.map((r, i) => `<button type="button" data-geo-pick="${i}">${esc(r.label)}</button>`).join("");
	box.onclick = async (e) => {
		const b = e.target.closest("[data-geo-pick]"); if (!b) return;
		const r = rows[+b.dataset.geoPick];
		box.hidden = true;
		const m = await startMap(form, [r.lat, r.lng]);
		m?.setView([r.lat, r.lng], r.street ? 17 : 15);
	};
}

async function shortLookup(form) {
	const input = field(form, "national_address");
	const code = (input.value || "").replace(/\s/g, "").toUpperCase();
	input.value = code;
	if (!/^[A-Z]{4}\d{4}$/.test(code)) { found(form, esc(ERR.invalid()), "warn"); input.classList.add("is-invalid"); return; }
	const btn = $("[data-short-lookup]", form);
	btn.disabled = true; btn.classList.add("is-loading");
	try {
		const r = await api("darat.storefront.api.geo_short_address", { code }, "POST");
		if (r.error) { found(form, esc(errText(r.error)), "warn"); return; }
		fill(form, r, { force: true });
		found(form, `✓ ${esc([r.building_number, r.street, r.district, r.city_text].filter(Boolean).join("، "))}`, "ok");
		if (r.lat && r.lng) { const m = await startMap(form, [r.lat, r.lng]); m?.setView([r.lat, r.lng], 18); }
	} catch (e) { found(form, esc(e.message), "warn"); }
	finally { btn.disabled = false; btn.classList.remove("is-loading"); }
}

// ---------------------------------------------------------------- saved address (this device)

function readForm(form) {
	const fd = new FormData(form);
	return Object.fromEntries(FIELDS.map((k) => [k, (fd.get(k) || "").toString().trim()]));
}

export function rememberAddress(form) {
	const a = readForm(form);
	if (a.city && a.street) store(SAVED_KEY, { ...a, saved: Date.now() });
}

function savedCard(form, a) {
	const box = $("[data-addr-saved]", form);
	const labels = { home: t("المنزل", "Home"), work: t("العمل", "Work"), other: t("عنوان محفوظ", "Saved address") };
	const cityName = (cityRows || []).find((c) => c[0] === a.city)?.[isAr ? 1 : 0] || a.city;
	box.innerHTML = `<div class="ds-addr__card">${icon("pin")}<div class="ds-addr__cardtxt"><b>${esc(labels[a.address_label] || labels.other)}</b>
		<span>${esc([a.building_number, a.street].filter(Boolean).join(" "))}${a.unit_number ? " - " + esc(a.unit_number) : ""}</span>
		<span>${esc([a.district, cityName].filter(Boolean).join("، "))}${a.national_address ? ` · <span dir="ltr">${esc(a.national_address)}</span>` : ""}</span></div>
		<button type="button" class="ds-btn ds-btn--ghost ds-addr__edit" data-addr-edit>${icon("refresh")}<span>${esc(t("تعديل أو عنوان آخر", "Edit or use another"))}</span></button></div>`;
	box.hidden = false;
	$("[data-addr-form]", form).hidden = true;
	box.querySelector("[data-addr-edit]").onclick = async () => {
		box.hidden = true; $("[data-addr-form]", form).hidden = false;
		await startMap(form, a.lat && a.lng ? [+a.lat, +a.lng] : null);
	};
}

function restore(form, a) {
	FIELDS.forEach((k) => {
		if (k === "address_label") { const r = form.querySelector(`[name=address_label][value="${a[k]}"]`); if (r) r.checked = true; return; }
		const el = field(form, k);
		if (el && a[k]) {
			el.value = a[k]; el.dataset.userEdited = "1";
			if (el.tagName === "SELECT") el.dispatchEvent(new Event("change", { bubbles: true }));
		}
	});
}

// ---------------------------------------------------------------- entry

export async function initDeliveryAddress(form) {
	if (started) { if (map) setTimeout(() => map.invalidateSize(), 60); return; }
	started = true;
	await loadCities(form);
	$$("[data-geo-field], [name=unit_number], [name=landmark]", form).forEach((el) => {
		el.addEventListener("input", () => { el.dataset.userEdited = "1"; });
		if (el.tagName === "SELECT") el.addEventListener("change", () => { if (!el.dataset.filling) el.dataset.userEdited = "1"; });
	});
	$("[data-geo-locate]", form)?.addEventListener("click", () => locate(form));
	$("[data-geo-search]", form)?.addEventListener("click", () => search(form));
	$("[data-geo-q]", form)?.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); search(form); } });
	$("[data-short-lookup]", form)?.addEventListener("click", () => shortLookup(form));
	$("[data-short]", form)?.addEventListener("keydown", (e) => { if (e.key === "Enter" && $("[data-short-lookup]", form)) { e.preventDefault(); shortLookup(form); } });

	// a signed-in customer's account address first, then the one remembered on this device
	let fromAccount = null;
	try { fromAccount = JSON.parse(form.dataset.accountAddress || "null"); } catch (e) { /* none */ }
	const saved = (fromAccount && fromAccount.city && fromAccount.street) ? fromAccount : store(SAVED_KEY);
	if (saved && saved.city && saved.street) {
		restore(form, saved);
		savedCard(form, saved);
		return;  // the map loads only if they choose to edit
	}
	startMap(form, null);
}
